import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import Big from 'big.js'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { IStakes } from '../../../hooks/useMasterContract'

import useWeb3 from '../../../hooks/useWeb3'

import { amountFromWei } from '../../../utils/amount'
import { numberWithCommas } from '../../../utils/commaSeperators'
import { Box } from '../../../shared_components/UI/Box'
import { getRgbaValue } from '../../../utils/styling'
import { BoxSubHeading } from '../parts'

import { IPool } from '../../../types/reducerTypes'
import { selectAccount } from '../../../modules/web3/selectors'
import useDynamicMaster from '../../../hooks/useDynamicMaster'

import { USDB_DECIMALS } from '../../../config'
import { toast } from 'react-toastify'
import useNetworkId from '../../../hooks/useNetworkId'

import { getPoolBySlug } from '../../../utils/pools'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { useGetContractByNameQuery } from '../../../modules/web3/api'
import { createContract } from '../../Dashboard/Launches'
import { Link, useHistory } from 'react-router-dom'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'

//

const Container = styled(Box)<{ showBottom?: boolean }>`
  min-height: ${({ showBottom }) => (showBottom ? 110 : 90)}px;
  flex-direction: column;
  border-top: 1px solid
    ${({ theme }) => getRgbaValue(theme.semantics.zap.surface.primary, 0.2)};
  padding: 16px 0;
  position: relative;
  margin-top: 1.5rem;
  span.notice {
    text-align: center;
    opacity: 0.7;
    font-size: 11px;
  }
  .button-link {
    width: 100%;
  }
`

const Row = styled(Box)`
  height: 74px;
  flex-direction: column;
  position: relative;
`

const Inner = styled(Box)`
  max-height: 34px;
`

const TableRow = styled(Box)<{ noBorder?: boolean }>`
  border-bottom: 1px solid
    ${({ theme, noBorder }) =>
      noBorder
        ? 'transparent'
        : theme.isDark
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'};
  height: 20px;
  flex: unset;
  align-items: center;
`

interface Props {
  pool: IPool
  stakes: IStakes
  isComplete: boolean
  fetchStakes: () => Promise<void>
  setIsLoading: Dispatch<SetStateAction<boolean>>
}

const ClaimEstimates: FC<Props> = ({
  pool,
  stakes,
  isComplete,
  fetchStakes,
  setIsLoading,
}) => {
  const {
    updateBalance,
    contracts: { master, masterRpc },
  } = useWeb3()

  const history = useHistory()

  const poolAddress = pool?.poolAddress

  const account = useSelector(selectAccount)

  const [tokensAvailable, setTokensAvailable] = useState<Big | null>(null)
  const [refundInUSDC, setRefundInUSBC] = useState<Big | null>(null)
  const [tokensToClaim, setTokensToClaim] = useState<Big | null>(null)
  const [canClaim, setCanClaim] = useState(false)
  const [hasClaimed, setHasClaimed] = useState(false)
  const [hasClaimedLP, setHasClaimedLP] = useState(false)
  const [shouldPoll, setShouldPoll] = useState(false)

  const poolSettings = pool?.poolSettings

  const createdBy = poolSettings?.createdBy
  const isTenMinutesAfterEnd =
    pool?.privateSale?.end &&
    new Date(
      pool?.privateSale?.end ?? new Date().getTime() + 100000,
    ).getTime() +
      660000 <
      Date.now()

  const isCurrentCreator = createdBy?.toLowerCase() === account?.toLowerCase()

  const [poolFailed, setPoolFailed] = useState(false)

  const { wrongNetwork } = useNetworkId()
  const isEthRaise = pool?.poolSettings?.raiseCurrency === 'eth'
  const isLabsPool = !!pool?.isVetted

  const contractToFetch = isLabsPool
    ? 'TokenSale'
    : `TokenSale${isEthRaise ? 'ETH' : 'USDB'}`

  const dynamicMaster = useDynamicMaster(
    pool?.poolAddress ?? '',
    undefined,
    contractToFetch,
  )

  const { networkId } = useNetworkId()
  const { data: tokenSaleUsdbContractData } = useGetContractByNameQuery({
    name: contractToFetch,
    chainId: networkId,
  })

  const tokenSaleAbi = tokenSaleUsdbContractData?.contract?.abi

  const claimTo = async () => {
    try {
      history.push(`/dashboard/launch`)
    } catch (e) {
      console.error(e)
      toast.error('Error claiming tokens')
    } finally {
      setIsLoading(false)
    }
  }
  const claimLP = async () => {
    try {
      history.push(`/dashboard/launch`)
    } catch (e) {
      console.error(e)
      toast.error('Error claiming tokens')
    } finally {
      setIsLoading(false)
    }
  }

  const { provider } = useWeb3()
  const checkClaimEstimates = useCallback(async () => {
    try {
      const state = isLabsPool
        ? await dynamicMaster?.getState()
        : await createContract(tokenSaleAbi, poolAddress, provider).state()

      const totalSold = state[0]
      const supply = state[1]
      const userBought = stakes?.amount?.toString()

      const claimAmounts = await dynamicMaster?.canClaim(account)

      const claimInTokens = claimAmounts[0]
      const claimInUSDB = claimAmounts[1]

      if (!claimAmounts) {
        setCanClaim(false)
        return
      }

      const allZero = !Number(claimInTokens) && !Number(claimInUSDB)

      if (allZero) {
        setCanClaim(false)
        return
      }

      setCanClaim(true)
      if (totalSold && userBought && supply) {
        const supplyInWei = new Big(supply)

        const userBoughtFromWei = amountFromWei(userBought, USDB_DECIMALS)
        const ratio = supplyInWei.div(totalSold)

        const isOversold = ratio.toNumber() < 1

        setTokensAvailable(
          isLabsPool
            ? new Big(stakes?.amount)
                .times(new Big(pool?.privateSale?.price))
                .times(isOversold ? ratio : new Big(1))
            : isOversold
            ? amountFromWei(stakes?.amount.times(ratio), USDB_DECIMALS).div(
                new Big(pool?.privateSale?.price),
              )
            : amountFromWei(stakes?.amount, USDB_DECIMALS).div(
                new Big(pool?.privateSale?.price),
              ),
        )
        setTokensToClaim(amountFromWei(claimInTokens, Number(pool.decimals)))
        if (isOversold) {
          const dollarRefund = userBoughtFromWei.minus(
            new Big(userBoughtFromWei.times(ratio)),
          )

          setCanClaim(true)
          setRefundInUSBC(dollarRefund)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }, [
    dynamicMaster,
    pool?.privateSale?.price,
    stakes?.amount,
    account,
    pool?.decimals,
    poolAddress,
    isLabsPool,
    tokenSaleAbi,
    provider,
  ])

  const pollPoolData = useCallback(async () => {
    try {
      if (!pool?.slug) return
      const { slug } = pool
      const {
        data: { pool: poolFromCms },
      } = await getPoolBySlug(slug)

      if (poolFromCms.slug !== slug) throw new Error('Pool not found')
      if (poolFromCms?.hasFailed) {
        setPoolFailed(true)
        setShouldPoll(false)
        return
      }
      if (!poolFromCms?.hasCollectedUsdb) return

      setShouldPoll(false)
      checkClaimEstimates()
    } catch (e) {
      console.error(e)
    }
  }, [pool, checkClaimEstimates])

  const poolClosed =
    !!pool?.privateSale?.end &&
    new Date(pool?.privateSale?.end).getTime() < Date.now()

  useEffect(() => {
    if (!poolClosed || isLabsPool) return
    setShouldPoll(true)
  }, [poolClosed, isLabsPool])

  useEffect(() => {
    let timer = null
    if (!shouldPoll) {
      return () => {
        clearInterval(timer)
        timer = null
      }
    }
    pollPoolData()
    timer = setInterval(() => {
      pollPoolData()
    }, 10000)
    return () => {
      timer = null
      clearInterval(timer)
    }
  }, [shouldPoll, pollPoolData])

  useEffect(() => {
    if (!poolAddress || !dynamicMaster || wrongNetwork || !tokenSaleAbi) return
    checkClaimEstimates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolAddress, master, stakes, masterRpc, pool, tokenSaleAbi])

  useEffect(() => {
    if (!stakes?.claimed) return
    setHasClaimed(true)
  }, [stakes])

  const hasStake = stakes?.amount && parseFloat(stakes.amount.toString()) > 0

  const raiseCurrency = (
    pool?.poolSettings?.raiseCurrency ?? 'busd'
  ).toUpperCase()

  if (!hasStake) return <></>

  return (
    <Container showBottom={canClaim} g={0} fu>
      {hasStake && (
        <Row fu g={4}>
          <Box marginBottom={8} fu>
            <BoxSubHeading fullOpacity>Your Token Purchase</BoxSubHeading>
            <BoxSubHeading fullOpacity>Your Token Purchase</BoxSubHeading>
          </Box>
          <TableRow justify="space-between" noBorder>
            <BoxSubHeading>Amount paid</BoxSubHeading>
            <Inner justify="flex-end" align="center">
              <BoxSubHeading fullOpacity>
                {numberWithCommas(
                  isLabsPool
                    ? stakes?.amount?.toString()
                    : amountFromWei(
                        stakes?.amount?.toString(),
                        USDB_DECIMALS,
                      ).toNumber(),
                )}
              </BoxSubHeading>
              <BoxSubHeading>&nbsp;{raiseCurrency}</BoxSubHeading>
            </Inner>
          </TableRow>
          <TableRow justify="space-between" noBorder={!refundInUSDC}>
            <BoxSubHeading>
              {isComplete ? '' : 'Estimated '}Tokens
            </BoxSubHeading>
            <Inner justify="flex-end" align="center">
              <BoxSubHeading fullOpacity>
                {tokensAvailable
                  ? showDecimalsIfNeeded(
                      tokensAvailable.toNumber(),
                    ).toLocaleString()
                  : 'TBC'}{' '}
              </BoxSubHeading>
              <BoxSubHeading>&nbsp;{`${pool?.ticker}`}</BoxSubHeading>
            </Inner>
          </TableRow>
        </Row>
      )}
      {isLabsPool ? (
        poolClosed && !!hasStake ? (
          <Box style={{ marginTop: 32 }}>
            <Link to="/dashboard" style={{ width: '100%' }}>
              <ThemedButton $styleType="primary" w="100%">
                Visit Dashboard
              </ThemedButton>
            </Link>
          </Box>
        ) : (
          <></>
        )
      ) : (
        <>
          {poolFailed && shouldPoll && (
            <Box marginTop={32}>
              <ThemedButton $styleType="glass" w="calc(100% - 0.5rem)">
                <AnimatedLoader onlyWhite size={16} />
              </ThemedButton>
            </Box>
          )}
          {poolFailed && !shouldPoll && (
            <Box marginTop={32} dc g={16}>
              <Box c>
                <span className="notice">
                  This pool failed to reach the minimum raise amount
                </span>
              </Box>
              <Link to="/dashboard/launch" className="button-link">
                <ThemedButton $styleType="glass" w="calc(100% - 0.5rem)">
                  CHECK REFUND STATUS
                </ThemedButton>
              </Link>
            </Box>
          )}
          {isTenMinutesAfterEnd && isCurrentCreator && !shouldPoll && (
            <Box marginTop={32} dc g={16}>
              <ThemedButton
                $styleType="glass"
                w="calc(100% - 0.5rem)"
                onClick={hasClaimedLP ? null : claimLP}
              >
                {hasClaimedLP ? 'LP Tokens Claim' : 'Claim LP Token'}
              </ThemedButton>
            </Box>
          )}
          {!poolFailed && shouldPoll && (refundInUSDC || tokensToClaim) && (
            <ThemedButton $styleType="glass" w="calc(100% - 0.5rem)">
              <AnimatedLoader onlyWhite size={16} />
            </ThemedButton>
          )}

          {!shouldPoll &&
            !poolFailed &&
            (refundInUSDC || (tokensToClaim && isComplete)) && (
              <TableRow justify="space-between" noBorder fu>
                <Box justify="space-between" dc g={32}>
                  {refundInUSDC && (
                    <Box ac jsb>
                      <BoxSubHeading>
                        {isComplete ? '' : 'Estimated '}Refund
                      </BoxSubHeading>
                      <span>
                        <BoxSubHeading fullOpacity>
                          {ifIntegerReturnIntegerOtherwiseTwoDecimalPlaces(
                            refundInUSDC.toFixed(2),
                          )}
                        </BoxSubHeading>
                        <BoxSubHeading>&nbsp;USDB</BoxSubHeading>
                      </span>
                    </Box>
                  )}

                  {isComplete && (
                    <Box marginBottom={-16} marginTop={16}>
                      <ThemedButton
                        onClick={claimTo}
                        $styleType={hasClaimed ? 'glass' : 'yellow'}
                        disabled={hasClaimed}
                        w="calc(100% - 0.5rem)"
                      >
                        Claim{hasClaimed ? 'ed' : ''}
                      </ThemedButton>
                    </Box>
                  )}
                </Box>
              </TableRow>
            )}
        </>
      )}
    </Container>
  )
}

export default ClaimEstimates

export function ifIntegerReturnIntegerOtherwiseTwoDecimalPlaces(
  value: number | string,
): number {
  return Number.isInteger(Number(value))
    ? Number(value)
    : Number(Number(value).toFixed(2))
}
